import React, { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { authApi } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import bgmain from "../../src/Images/main.jpg";

const SignUp = () => {
  const [formData, setFormData] = useState({
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value.trim(),
    }));
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError("");

    if (!formData.email) {
      alert("Email is required!");
      return;
    }
    if (!formData.phoneNumber) {
      alert("Mobile number is required!");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      alert("Passwords do not match!");
      return;
    }
    if (!formData.phoneNumber.startsWith("+")) {
      alert("Please enter a valid mobile number.");
      return;
    }

    try {
      setLoading(true);
      const data = await authApi.signup({
        role: "customer",
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        password: formData.password,
      });

      login(data);

      // reset form
      setFormData({
        email: "",
        phoneNumber: "",
        password: "",
        confirmPassword: "",
      });

      // since default is customer, redirect to home
      navigate("/");
    } catch (error) {
      console.error("Error:", error);
      const status = error?.status;
      const data = error?.data || {};
      if (data?.code === "VALIDATION_ERROR") {
        const first =
          Array.isArray(data.errors) && data.errors.length > 0
            ? data.errors[0]?.message
            : null;
        setGeneralError(first || data?.message || "Validation error");
      } else if (status === 409 || data?.code === "CONFLICT") {
        setGeneralError(
          data?.message || "Email or phone number already exists"
        );
      } else {
        setGeneralError(data?.message || "Signup failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-h-screen w-full flex items-center ">
      <img
        src={bgmain}
        alt="Hero Background"
        className="absolute min-h-screen inset-0 w-full h-full object-cover z-[-10] blur-sm"
      />
      <div className="w-11/12 md:w-2/5 mx-auto px-6 py-10 poppins-regular  bg-gray-100/30 backdrop-blur-sm rounded-lg">
        <h2 className="text-3xl poppins-bold uppercase text-center mb-8 text-gray-800">
          Sign Up
        </h2>

        {/* Service provider link */}
        <div className="mb-6 text-center">
          <span className="text-lg font-medium text-gray-900">
            Have a business?{" "}
          </span>
          <Link
            to="/provider/signup"
            className="ml-1 inline-block text-lg font-semibold text-blue-800 hover:text-blue-900 "
          >
            Signup here
          </Link>
        </div>

        {generalError && (
          <div className="mb-4 p-3 rounded bg-red-50 text-red-700 border border-red-200">
            {generalError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email */}
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-900 mb-1">
              Email
            </label>
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Mobile */}
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-900 mb-1">
              Mobile Number
            </label>
            <PhoneInput
              country={"in"}
              value={formData.phoneNumber}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, phoneNumber: `+${value}` }))
              }
              inputStyle={{
                width: "100%",
                height: "42px",
                fontSize: "16px",
                borderRadius: "0.5rem",
                border: "1px solid #d1d5db",
              }}
              buttonStyle={{
                borderRadius: "0.5rem 0 0 0.5rem",
                border: "1px solid #d1d5db",
                backgroundColor: "#fff",
              }}
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-900 mb-1">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-900 mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Submit */}
          <div className="text-right">
            <button
              type="submit"
              disabled={loading}
              className={`bg-gradient-to-r from-blue-500 to-blue-700 uppercase px-6 py-2 text-white rounded-lg mulish-bold transition ${
                loading
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:text-blue-200"
              }`}
            >
              {loading ? "Creating..." : "Create Account"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default SignUp;
